/**
 * Banco de preguntas por caso clínico
 */

import { QuizCaseConfig } from './types';

export const QUIZ_CASES: Record<string, QuizCaseConfig> = {
  iam: {
    caseId: 'iam',
    hiddenNodeTypes: ['diagnosis'],
    explorationTimeSeconds: 45,
    passingScore: 60,
    questions: [
      {
        id: 'iam-dx',
        type: 'diagnosis',
        text: '¿Cuál es el diagnóstico más probable para este paciente?',
        options: [
          { label: 'Infarto agudo de miocardio', value: 'iam' },
          { label: 'Tromboembolia pulmonar', value: 'tep' },
          { label: 'Disección aórtica', value: 'diseccion' },
          { label: 'Pericarditis aguda', value: 'pericarditis' },
        ],
        correctIndex: 0,
        explanation: 'El dolor torácico opresivo con irradiación a brazo izquierdo, taquicardia (FC 110), hipotensión (PA 90) y troponina T marcadamente elevada (850 ng/L, normal <14) configuran un cuadro clásico de IAM.',
        relatedNodeIds: ['sintoma-1', 'vital-1', 'lab-1'],
      },
      {
        id: 'iam-triage',
        type: 'triage',
        text: '¿Qué prioridad de triage asignarías?',
        options: [
          { label: 'I — Atención inmediata (rojo)', value: 'immediate' },
          { label: 'II — Puede esperar (ámbar)', value: 'delayed' },
          { label: 'III — Ambulatorio (verde)', value: 'minor' },
          { label: 'IV — Expectante', value: 'expectant' },
        ],
        correctIndex: 0,
        explanation: 'Un IAM con inestabilidad hemodinámica (hipotensión, taquicardia) requiere atención inmediata. Tiempo puerta-balón < 90 minutos es crítico.',
        relatedNodeIds: ['vital-1', 'vital-2'],
      },
      {
        id: 'iam-pattern',
        type: 'pattern',
        text: '¿Qué hallazgo de laboratorio es más específico para confirmar el diagnóstico?',
        options: [
          { label: 'Troponina T elevada', value: 'troponin' },
          { label: 'Leucocitosis', value: 'wbc' },
          { label: 'Creatinina elevada', value: 'creat' },
          { label: 'PCR elevada', value: 'pcr' },
        ],
        correctIndex: 0,
        explanation: 'La troponina T (o I) es el biomarcador más específico de daño miocárdico. Un valor de 850 ng/L (normal <14) confirma necrosis miocárdica.',
        relatedNodeIds: ['lab-1'],
      },
      {
        id: 'iam-next',
        type: 'next_step',
        text: '¿Cuál es la siguiente acción más importante?',
        options: [
          { label: 'Activar laboratorio de hemodinamia', value: 'cath' },
          { label: 'Solicitar radiografía de tórax', value: 'rx' },
          { label: 'Administrar paracetamol', value: 'paracetamol' },
          { label: 'Esperar segunda troponina en 6 horas', value: 'wait' },
        ],
        correctIndex: 0,
        explanation: 'Con un STEMI, la prioridad es la reperfusión coronaria urgente. Se debe activar el laboratorio de hemodinamia para angioplastía primaria (tiempo puerta-balón < 90 min).',
      },
    ],
  },

  sepsis: {
    caseId: 'sepsis',
    hiddenNodeTypes: ['diagnosis'],
    explorationTimeSeconds: 45,
    passingScore: 60,
    questions: [
      {
        id: 'sepsis-dx',
        type: 'diagnosis',
        text: '¿Cuál es el diagnóstico más probable?',
        options: [
          { label: 'Sepsis / Shock séptico', value: 'sepsis' },
          { label: 'Meningitis bacteriana', value: 'meningitis' },
          { label: 'Golpe de calor', value: 'heat' },
          { label: 'Crisis tirotóxica', value: 'thyroid' },
        ],
        correctIndex: 0,
        explanation: 'Fiebre 39.2°C + confusión mental + hipotensión (PA 85/50) + lactato elevado (4.2 mmol/L) cumplen criterios de Sepsis-3 con disfunción orgánica. El lactato > 2 + necesidad de vasopresores lo clasifican como shock séptico.',
        relatedNodeIds: ['sintoma-1', 'sintoma-2', 'vital-1', 'lab-1'],
      },
      {
        id: 'sepsis-triage',
        type: 'triage',
        text: '¿Qué prioridad de triage asignarías?',
        options: [
          { label: 'I — Atención inmediata (rojo)', value: 'immediate' },
          { label: 'II — Puede esperar (ámbar)', value: 'delayed' },
          { label: 'III — Ambulatorio (verde)', value: 'minor' },
          { label: 'IV — Expectante', value: 'expectant' },
        ],
        correctIndex: 0,
        explanation: 'El shock séptico tiene mortalidad >40% y requiere reanimación agresiva inmediata: fluidos IV, antibióticos empíricos dentro de la primera hora, y vasopresores si no responde a volumen.',
        relatedNodeIds: ['vital-1', 'lab-1'],
      },
      {
        id: 'sepsis-score',
        type: 'pattern',
        text: '¿Qué score clínico es más útil para evaluar sepsis en urgencias?',
        options: [
          { label: 'qSOFA (Quick SOFA)', value: 'qsofa' },
          { label: 'APGAR', value: 'apgar' },
          { label: 'Escala de Glasgow', value: 'gcs' },
          { label: 'Score de Wells', value: 'wells' },
        ],
        correctIndex: 0,
        explanation: 'El qSOFA evalúa: FR ≥ 22, PA sistólica ≤ 100, y alteración del estado mental. Con ≥ 2 criterios positivos hay alta sospecha de sepsis. Este paciente cumple los 3.',
        relatedNodeIds: ['sintoma-2', 'vital-1'],
      },
      {
        id: 'sepsis-next',
        type: 'next_step',
        text: '¿Cuál es la acción más urgente?',
        options: [
          { label: 'Antibióticos empíricos + fluidos IV en la primera hora', value: 'abx' },
          { label: 'TAC de cráneo', value: 'tac' },
          { label: 'Hemocultivos y esperar resultado', value: 'wait' },
          { label: 'Transferir a piso general', value: 'floor' },
        ],
        correctIndex: 0,
        explanation: 'La "hora dorada" de la sepsis exige: hemocultivos + antibióticos empíricos + bolo de cristaloides 30 mL/kg en la PRIMERA HORA. Cada hora de retraso en antibióticos aumenta la mortalidad ~7%.',
      },
    ],
  },

  epoc: {
    caseId: 'epoc',
    hiddenNodeTypes: ['diagnosis'],
    explorationTimeSeconds: 40,
    passingScore: 60,
    questions: [
      {
        id: 'epoc-dx',
        type: 'diagnosis',
        text: '¿Cuál es el diagnóstico más probable?',
        options: [
          { label: 'Exacerbación aguda de EPOC', value: 'epoc' },
          { label: 'Neumonía adquirida en comunidad', value: 'nac' },
          { label: 'Insuficiencia cardíaca descompensada', value: 'ic' },
          { label: 'Crisis asmática', value: 'asma' },
        ],
        correctIndex: 0,
        explanation: 'Paciente con antecedente de EPOC de 10 años que presenta disnea progresiva, taquipnea (FR 28) e hipoxemia (SpO2 86%). El cuadro clásico de exacerbación aguda de EPOC.',
        relatedNodeIds: ['hist-1', 'sintoma-1', 'vital-1', 'vital-2'],
      },
      {
        id: 'epoc-triage',
        type: 'triage',
        text: '¿Qué prioridad de triage asignarías?',
        options: [
          { label: 'I — Atención inmediata (rojo)', value: 'immediate' },
          { label: 'II — Puede esperar (ámbar)', value: 'delayed' },
          { label: 'III — Ambulatorio (verde)', value: 'minor' },
          { label: 'IV — Expectante', value: 'expectant' },
        ],
        correctIndex: 1,
        explanation: 'SpO2 86% es preocupante pero el paciente mantiene conciencia y hemodinámica estable. Requiere evaluación urgente pero no es inmediata si puede recibir O2 suplementario rápidamente.',
        relatedNodeIds: ['vital-2'],
      },
      {
        id: 'epoc-next',
        type: 'next_step',
        text: '¿Cuál es la primera intervención?',
        options: [
          { label: 'Oxígeno suplementario + broncodilatadores nebulizados', value: 'o2' },
          { label: 'Intubación orotraqueal inmediata', value: 'iot' },
          { label: 'Antibióticos IV empíricos', value: 'abx' },
          { label: 'Radiografía de tórax y esperar', value: 'rx' },
        ],
        correctIndex: 0,
        explanation: 'La prioridad es corregir la hipoxemia con O2 suplementario (meta SpO2 88-92% en EPOC) y aliviar el broncoespasmo con salbutamol + ipratropio nebulizados. La intubación se reserva para falla respiratoria refractaria.',
      },
    ],
  },
};
